// PROduce schedule convention: a "festival day" runs 06:00 → 05:59 next-day.
// A shift labeled event_date=X with filming_start < 06:00 actually happens
// on calendar day X+1. A shift with filming_end < filming_start crosses
// midnight within itself (e.g. 23:30-02:00).
//
// TS parity for the SQL helpers public.effective_shift_start_at /
// effective_shift_end_at (migration 20260524120000). Used by Analytics for
// per-assignment presence detection and duration calculation.
//
// TZ note: returned Date is in the browser's local timezone, parsed from
// 'YYYY-MM-DDTHH:MM:SS' (which JS reads as local). PROduce dashboards run
// in producer browsers, typically in the festival's own timezone, so this
// matches reality. If the producer browser is in a different zone than the
// festival, the comparison still works (presence_log timestamps come back
// in UTC; both sides convert to ms-since-epoch for comparison).

import { FESTIVAL_DAY_ROLLOVER_HOUR } from "./eventDays";

function parseTime(t: string): { h: number; m: number; s: number } {
  const [h, m = "0", s = "0"] = t.split(":");
  return { h: Number(h), m: Number(m), s: Number(s) };
}

function addDaysLocal(d: Date, days: number): Date {
  const next = new Date(d);
  next.setDate(next.getDate() + days);
  return next;
}

export function effectiveShiftStart(eventDate: string, filmingStart: string): Date {
  const { h, m, s } = parseTime(filmingStart);
  // Build local-midnight on event_date then shift forward to the actual hour.
  const anchor = new Date(`${eventDate}T00:00:00`);
  // Late-night shift (h < 6) actually starts on calendar day event_date + 1.
  const startDate = h < FESTIVAL_DAY_ROLLOVER_HOUR ? addDaysLocal(anchor, 1) : anchor;
  startDate.setHours(h, m, s, 0);
  return startDate;
}

export function effectiveShiftEnd(
  eventDate: string,
  filmingStart: string,
  filmingEnd: string,
): Date {
  const start = effectiveShiftStart(eventDate, filmingStart);
  const startTime = parseTime(filmingStart);
  const endTime = parseTime(filmingEnd);
  // End anchored on the SAME calendar day as start; add 1 day if the shift
  // crosses midnight within itself (e.g. 23:30 → 02:00).
  const end = new Date(start);
  end.setHours(endTime.h, endTime.m, endTime.s, 0);
  const startMin = startTime.h * 60 + startTime.m;
  const endMin = endTime.h * 60 + endTime.m;
  if (endMin < startMin) {
    end.setDate(end.getDate() + 1);
  }
  return end;
}

/** Duration of a shift in minutes, midnight-aware. */
export function shiftDurationMinutes(filmingStart: string, filmingEnd: string): number {
  const startTime = parseTime(filmingStart);
  const endTime = parseTime(filmingEnd);
  const startMin = startTime.h * 60 + startTime.m;
  let endMin = endTime.h * 60 + endTime.m;
  if (endMin < startMin) endMin += 24 * 60;
  return endMin - startMin;
}

// ─────────────────────────────────────────────────────────────────────────────
// "Parts" variants for callers that already have a TZ-aware primitive (e.g.
// wallClockToUtc(date, time, tz)) — these return the SHIFTED (date, time)
// tuple after applying the festival 06:00 cutoff. Use these when you want
// the briefing's exact TZ, not the browser's local TZ.
// ─────────────────────────────────────────────────────────────────────────────
export function addDaysISO(eventDate: string, days: number): string {
  const d = new Date(`${eventDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * Festival "day" bounds — the window from 06:00 on event_date to 06:00 on
 * event_date+1, matching the schedule day convention. Returns (date, time)
 * tuples to be paired with wallClockToUtc(briefing_tz) by the caller.
 */
export function festivalDayBoundsParts(eventDate: string): {
  start: { date: string; time: string };
  end: { date: string; time: string };
} {
  return {
    start: { date: eventDate, time: "06:00:00" },
    end: { date: addDaysISO(eventDate, 1), time: "06:00:00" },
  };
}

export function effectiveShiftStartParts(
  eventDate: string,
  filmingStart: string,
): { date: string; time: string } {
  const { h } = parseTime(filmingStart);
  if (h < FESTIVAL_DAY_ROLLOVER_HOUR) {
    return { date: addDaysISO(eventDate, 1), time: filmingStart };
  }
  return { date: eventDate, time: filmingStart };
}

export function effectiveShiftEndParts(
  eventDate: string,
  filmingStart: string,
  filmingEnd: string,
): { date: string; time: string } {
  const start = effectiveShiftStartParts(eventDate, filmingStart);
  const sMin = parseTime(filmingStart).h * 60 + parseTime(filmingStart).m;
  const eMin = parseTime(filmingEnd).h * 60 + parseTime(filmingEnd).m;
  if (eMin < sMin) {
    return { date: addDaysISO(start.date, 1), time: filmingEnd };
  }
  return { date: start.date, time: filmingEnd };
}
